/**
 * The 9 lifecycle roles used by every studio in InteriorOS.
 *
 * `superAdmin` is reserved for the org owner — exactly one per
 * organization. Every other member has exactly one of the
 * remaining 8 roles.
 *
 * Definition lives here so mobile, web, and Cloud Functions all
 * read the same union. If you need to add / remove / rename a
 * role, edit this file ONCE — the TypeScript compiler will fail
 * every consumer until they're updated, which is exactly what
 * stops drift.
 */
export type RoleKey =
  | 'superAdmin'
  | 'admin'
  | 'manager'
  | 'accountant'
  | 'siteEngineer'
  | 'supervisor'
  | 'designer'
  | 'viewer'
  | 'client';

/** A role anyone can be assigned. `superAdmin` is excluded —
 *  ownership transfers via a separate ceremony (`setOrgOwner`-
 *  style callable), never via the role picker. */
export type AssignableRole = Exclude<RoleKey, 'superAdmin'>;

/** Display labels — what shows up in pickers, member lists,
 *  badges, audit logs. Add a key here whenever you add a role
 *  to `RoleKey` (the `Record<RoleKey, …>` type forces it). */
export const ROLE_LABELS: Record<RoleKey, string> = {
  superAdmin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  accountant: 'Accountant',
  siteEngineer: 'Site Engineer',
  supervisor: 'Supervisor',
  designer: 'Designer',
  viewer: 'Viewer',
  client: 'Client',
};

/** One-line descriptions shown in tooltips / role-picker chips
 *  / docs. */
export const ROLE_DESCRIPTIONS: Record<RoleKey, string> = {
  superAdmin: 'Studio owner. Full access; manages other admins.',
  admin: 'Full operational access; cannot manage other admins.',
  manager:
    'Runs projects, money, and team (sub-roles only). No billing or studio-profile edit.',
  accountant: 'Finance only — transactions, parties, reports.',
  siteEngineer: 'Technical execution — tasks, designs, materials, DPR.',
  supervisor: 'On-site execution — attendance, DPR, task updates.',
  designer:
    'Design work — timeline, laminates, files. No finance or CRM.',
  viewer: 'Org-wide read-only access.',
  client: 'Per-project read-only on assigned projects.',
};

// ── Single-role tier checks ──────────────────────────────────
//
// Tiny helpers so callers don't write `role === 'superAdmin'`
// 47 times across the codebase. They also encode the role
// hierarchy in one place — change the hierarchy here, every
// consumer follows.

export function isSuperAdmin(role: RoleKey | null | undefined): boolean {
  return role === 'superAdmin';
}

export function isAdmin(role: RoleKey | null | undefined): boolean {
  return role === 'admin';
}

export function isManager(role: RoleKey | null | undefined): boolean {
  return role === 'manager';
}
